// ======================================================================
// File: src/modules/ppe/components/routing/RoutingView.js
// Purpose:
//   Routing view per Order Item
//   - Execution snapshot
//   - Controlled override with audit
//   - ERP-safe (Phase 3)
// ======================================================================

import React, { useEffect, useState } from "react";
import ppeOrderService from "../../services/ppeOrderService";
import RoutingOverrideDrawer from "./RoutingOverrideDrawer";

export default function RoutingView({ items = [] }) {
  return (
    <div>
      {items.length === 0 && (
        <div className="text-muted">
          No order items available for routing.
        </div>
      )}

      {items.map((item) => (
        <RoutingCard key={item.id} item={item} />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Routing Card (per Order Item)
// ----------------------------------------------------------------------
function RoutingCard({ item }) {
  const [routing, setRouting] = useState(null);
  const [operations, setOperations] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showOverride, setShowOverride] = useState(false);

  async function loadRouting() {
    setLoading(true);
    try {
      const routingRes =
        await ppeOrderService.getRoutingAssignment(item.id);

      setRouting(routingRes || null);
      setOperations(routingRes?.operations || []);
    } catch (e) {
      setRouting(null);
      setOperations([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRouting();
  }, [item.id]);

  return (
    <div className="card mb-3">
      {/* ================= HEADER ================= */}
      <div
        className="card-header"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded((e) => !e)}
      >
        <strong>
          {expanded ? "▼" : "▶"} Item #{item.item_seq_no} | Product Variant #{item.product_variant_id}
        </strong>

        <div className="text-muted small">
          Quantity: {item.quantity}
        </div>

        {routing && (
          <div className="mt-1">
            <span className="badge bg-secondary me-1">
              Routing {routing.routing_master_id}
            </span>
            <span className="badge bg-info me-1">
              Version {routing.routing_version_no}
            </span>
            <span className="badge bg-dark">
              {routing.assignment_reason}
            </span>
          </div>
        )}
      </div>

      {/* ================= BODY ================= */}
      {expanded && (
        <div className="card-body">
          {loading && <div>Loading routing…</div>}

          {!loading && !routing && (
            <div className="alert alert-warning mb-0">
              Routing not assigned yet.
            </div>
          )}

          {!loading && routing && (
            <>
              <div className="d-flex justify-content-between mb-2">
                <h6 className="mb-0">Operations</h6>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => setShowOverride(true)}
                >
                  Override Routing
                </button>
              </div>

              <table className="table table-bordered table-sm">
                <thead className="table-light">
                  <tr>
                    <th>Seq</th>
                    <th>Process</th>
                    <th>Skill</th>
                    <th>Type</th>
                    <th>Std Cycle Time</th>
                    <th>Setup Time</th>
                  </tr>
                </thead>
                <tbody>
                  {operations.length === 0 && (
                    <tr>
                      <td colSpan="6" className="text-center text-muted">
                        No operations defined
                      </td>
                    </tr>
                  )}

                  {operations.map((op) => {
                    // ======================================================
                    // ✅ STEP 1: PRIMARY MACHINE GROUP (SAFE FRONTEND ONLY)
                    // ======================================================
                    const primaryMachineGroup =
                      (op.machine_groups || []).find(
                        (mg) => mg.is_primary === true
                      )?.machine_group_name;

                    return (
                      <tr key={op.id}>
                        <td>{op.operation_seq_no}</td>

                        {/* Process + Machine Group Hint */}
                        <td>
                          <div>
                            <strong>
                              {op.process_name || `#${op.process_id}`}
                            </strong>
                          </div>

                          {/* ✅ Primary Machine Group (optional hint) */}
                          <div className="text-muted small">
                            {primaryMachineGroup
                              ? `Machine: ${primaryMachineGroup}`
                              : "Machine: —"}
                          </div>
                        </td>

                        {/* Skill */}
                        <td>
                          {op.skill_name || (
                            <span className="text-muted">
                              #{op.skill_id || "—"}
                            </span>
                          )}
                        </td>

                        {/* In-house / Outsourced */}
                        <td>
                          {op.is_outsourced ? (
                            <span className="badge bg-warning text-dark">
                              Outsourced
                            </span>
                          ) : (
                            <span className="badge bg-success">
                              In-house
                            </span>
                          )}
                        </td>

                        <td>{op.run_time_minutes}</td>
                        <td>{op.setup_time_minutes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              <div className="alert alert-secondary mt-3 mb-0">
                <strong>Note:</strong> Routing is an immutable execution snapshot.
              </div>
            </>
          )}
        </div>
      )}

      {/* ================= OVERRIDE DRAWER ================= */}
      <RoutingOverrideDrawer
        item={item}
        open={showOverride}
        onClose={() => setShowOverride(false)}
        onSuccess={loadRouting}
      />
    </div>
  );
}
